import { useState, useRef } from 'react';
import { runValidation, hasBlockers, getIssues } from '../lib/validation';
import { assetsApi } from '../services/assetsApi';
import type { ValidationResult } from '../lib/validation';
import type { Asset } from '../services/assetsApi';

export interface PendingUpload {
  id: string;
  localUrl: string;
  fileName: string;
  status: 'validating' | 'uploading' | 'done' | 'error';
}

interface UseImageUploadOptions {
  onUploadSuccess?: (asset: Asset) => void;
  onUploadError?: (fileName: string, message: string) => void;
}

export const useImageUpload = (options?: UseImageUploadOptions) => {
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [errors, setErrors] = useState<ValidationResult[]>([]);
  const [warnings, setWarnings] = useState<ValidationResult[]>([]);
  const nextId = useRef(0);

  const loadImage = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });

  const uploadFile = async (file: File): Promise<Asset | null> => {
    const localId = `pending-${nextId.current++}`;
    const localUrl = URL.createObjectURL(file);

    // 1. Instant thumbnail
    setPending(prev => [...prev, { id: localId, localUrl, fileName: file.name, status: 'validating' }]);

    try {
      // 2. Load image for dimensions + validation
      const img = await loadImage(file);

      // 3. Run validation
      const results = await runValidation('upload', { file, imageElement: img });
      const blockers = results.filter(r => r.blocker);
      const nonBlockers = getIssues(results).filter(r => !r.blocker);

      if (hasBlockers(results)) {
        setPending(prev => prev.filter(p => p.id !== localId));
        setErrors(blockers);
        setWarnings(nonBlockers);
        URL.revokeObjectURL(localUrl);
        return null;
      }

      // 4. Upload to server
      setPending(prev => prev.map(p => p.id === localId ? { ...p, status: 'uploading' as const } : p));

      const asset = await assetsApi.upload(file, {
        width: img.naturalWidth,
        height: img.naturalHeight,
      });

      // 5. Done — remove pending, notify
      setPending(prev => prev.filter(p => p.id !== localId));
      URL.revokeObjectURL(localUrl);
      setWarnings(nonBlockers);
      options?.onUploadSuccess?.(asset);
      return asset;
    } catch (err) {
      setPending(prev => prev.filter(p => p.id !== localId));
      URL.revokeObjectURL(localUrl);
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setErrors([{ key: 'server', status: 'error', blocker: true, title: 'Upload failed', message: msg }]);
      options?.onUploadError?.(file.name, msg);
      return null;
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;
    Array.from(files).forEach(f => { if (f.type.startsWith('image/')) uploadFile(f); });
    event.target.value = '';
  };

  const dismissErrors = () => setErrors([]);
  const dismissWarnings = () => setWarnings([]);

  return { pending, errors, warnings, uploadFile, handleFileSelect, dismissErrors, dismissWarnings };
};
